import { getIdToken } from "./auth/cognito.js";

// Same-origin by default: CloudFront routes /api/* to the API Gateway origin.
const API_URL = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");

export class AdminError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Extra structured data from the error envelope (e.g. studentId on a 409). */
    public fields?: Record<string, string>
  ) {
    super(message);
  }
}

/** Authenticated request to /admin/*. Attaches the Cognito ID token. */
export async function api<T = unknown>(
  path: string,
  opts: { method?: string; body?: unknown } = {}
): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new AdminError(401, "Your session expired - please sign in again.");
  const res = await fetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    headers: {
      Authorization: token,
      ...(opts.body ? { "content-type": "application/json" } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const err = data.error as { message?: string; fields?: Record<string, string> } | undefined;
    throw new AdminError(res.status, err?.message ?? "Request failed", err?.fields);
  }
  return data as T;
}

/** Upload a file straight to S3 via a presigned PUT URL. */
export async function uploadToS3(url: string, file: File): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "content-type": "application/pdf" },
    body: file,
  });
  if (!res.ok) throw new AdminError(res.status, "Upload failed");
}
